import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateContributionField } from "src/store/Goal-Based/ContributionAmountSlice";
import { updateGoalField } from "src/store/Goal-Based/GoalAmountSlice";
import { useGetRecommendedSmartfoliosQuery } from "src/store/middleware/api_v2";
import { RootStateType } from "src/store/rootReducer";
import { colors } from "src/theme/colors";
import { line, radii, space, withAlpha } from "src/theme/ui";
import styled from "styled-components";

interface RadioOption {
  label: string;
  value: string;
}

const RadioListContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${space.lg}px;
`;

/**
 * One objective.
 *
 * A chip, and only a chip. Each of these used to carry a 20px hollow circle
 * with a tick character absolutely positioned inside it — a radio control
 * drawn INSIDE a control that already changes appearance when it is chosen.
 * The chip's own state says everything the circle said, so the circle was a
 * second answer to the same question, and it made each of the nine about
 * half again as wide for it.
 *
 * The type came down with it: these were 1.2em at 600, which put a filter
 * above the fields it filters in the visual order of the page.
 */
const RadioButton = styled.button<{ isChecked: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  border-radius: ${radii.pill}px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  border: 1px solid
    ${({ isChecked }) => (isChecked ? colors.primary[500] : line.hairline)};
  background: ${({ isChecked }) =>
    isChecked ? withAlpha(colors.primary[500], 0.08) : colors.white};
  color: ${({ isChecked }) =>
    isChecked ? colors.primary[700] : colors.gray[700]};

  &:hover {
    border-color: ${({ isChecked }) =>
      isChecked ? colors.primary[500] : colors.gray[300]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 1px;
  }
`;

interface RadioListProps {
  options: RadioOption[];
  refetch: () => void;
}

const RadioList: React.FC<RadioListProps> = ({ options, refetch }) => {
  const [selectedValue, setSelectedValue] = React.useState<string | null>(null);
  const currentMenu = useSelector(
    (state: RootStateType) => state.menuReducer.currentState,
  );
  const goalAmount_objective = useSelector(
    (state: RootStateType) => state.GoalAmountReducer.objective,
  );
  const contribution_objective = useSelector(
    (state: RootStateType) => state.ContributionAmountReducer.objective,
  );
  const dispatch = useDispatch();

  const handleChange = (value: string) => {
    setSelectedValue(value);
    dispatch(updateGoalField({ name: "objective", value }));
    dispatch(updateContributionField({ name: "objective", value }));
    refetch();
  };
  useEffect(() => {
    if (currentMenu === "GoalAmount") {
      setSelectedValue(goalAmount_objective);
    } else {
      setSelectedValue(contribution_objective);
    }
  }, [currentMenu]);
  return (
    <RadioListContainer>
      {options.map((option) => (
        <RadioButton
          key={option.value}
          type="button"
          isChecked={option.value === selectedValue}
          aria-pressed={option.value === selectedValue}
          onClick={() => handleChange(option.value)}
        >
          {option.label}
        </RadioButton>
      ))}
    </RadioListContainer>
  );
};

export default RadioList;
